export function getDirtyValues<
    DirtyFields extends Record<string, unknown>,
    Values extends Record<keyof DirtyFields, unknown> & { id: number },
>(dirtyFields: DirtyFields, values: Values): Partial<typeof values> {
    const dirtyValues = Object.keys(dirtyFields).reduce((prev, key) => {
        // Unsure when RFH sets this to `false`, but omit the field if so.
        if (!dirtyFields[key]) return prev;

        let value;
        switch (true) {
            case Array.isArray(dirtyFields[key]):
                const dirtyFieldArray = dirtyFields[key] as Array<DirtyFields>;
                value = (values[key] as Array<Values>).map((item, idx) => {
                    if (dirtyFieldArray[idx]) {
                        return getDirtyValues(dirtyFieldArray[idx], item);
                    }
                    if (typeof item === 'object') {
                        return {
                            id: item.id,
                        };
                    }

                    return item;
                });
                break;
            case typeof dirtyFields[key] === 'object':
                value = getDirtyValues(dirtyFields[key] as DirtyFields, values[key] as Values);
                break;
            default:
                value = values[key];
        }

        return {
            ...prev,
            [key]: value,
        };
    }, {} as Values);

    if (values.id) {
        dirtyValues.id = values.id;
    }

    return dirtyValues;
}

export const getDirtyFormValues = <T extends Record<string, any> & { id?: number }>({
    formFields,
    dirtyFields,
}: {
    formFields: T;
    dirtyFields: Partial<Record<keyof T, unknown>>;
}): Partial<T> | null => {
    if (!formFields.id) {
        return formFields;
    }

    const dirtyFieldsKeys = Object.entries(dirtyFields).reduce(
        (acc, [key, value]) => {
            if (value) {
                acc.push(key);
            }
            return acc;
        },
        [] as Array<keyof T>,
    );
    if (!dirtyFieldsKeys.length) {
        return null;
    }

    const result = dirtyFieldsKeys.reduce((acc, key) => {
        acc[key] = formFields[key];
        return acc;
    }, {} as Partial<T>);

    if (formFields.id) {
        result.id = formFields.id;
    }

    return result;
};
